import React, { useEffect, useState } from "react";
import { Plus, Trash2, Send, Eye, X, Printer, Check, RotateCcw, FileText, Bell, Link2, Pencil, FileMinus, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError, API } from "@/lib/api";
import { money, fmtDate, LANGUAGES, langName } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, InvBadge } from "@/pages/shared";
import { uid } from "@/lib/uid";

const emptyItem = () => ({ _uid: uid(), description: "", qty: 1, unit_price: 0 });

export default function Facturen() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [guests, setGuests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [view, setView] = useState(null);
  const [pay, setPay] = useState(null);
  const [payMethod, setPayMethod] = useState("cash");
  const [dunFilter, setDunFilter] = useState("all");
  const [form, setForm] = useState({ guest_id: "", language: "nl", tax_rate: 21, currency: "EUR", items: [emptyItem()] });

  const load = async () => {
    try {
      const [i, g] = await Promise.all([api.get("/invoices"), api.get("/guests")]);
      setRows(i.data); setGuests(g.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setItem = (idx, key, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: key === "description" ? val : Number(val) };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const subtotal = form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const onGuest = (v) => {
    const g = guests.find((x) => x.id === v);
    setForm({ ...form, guest_id: v, language: g?.language || form.language });
  };

  const save = async () => {
    try {
      if (editingId) {
        await api.patch(`/invoices/${editingId}`, { items: form.items.map(({ _uid, ...it }) => it), tax_rate: form.tax_rate });
      } else {
        await api.post("/invoices", { ...form, items: form.items.map(({ _uid, ...it }) => it) });
      }
      setOpen(false);
      setEditingId(null);
      setForm({ guest_id: "", language: "nl", tax_rate: 21, currency: "EUR", items: [emptyItem()] });
      await load();
      toast.success(editingId ? t("saved") : t("created"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const isExported = (inv) => !!inv.accounting_exported_at;
  const isEditable = (inv) => !inv.accounting_exported_at && !inv.credited_by;

  const openEdit = (inv) => {
    setEditingId(inv.id);
    setForm({ guest_id: inv.guest_id || "", language: inv.language || "nl", tax_rate: inv.tax_rate ?? 21,
      currency: inv.currency || "EUR", items: (inv.items || []).map((it) => ({ ...it, _uid: uid() })) });
    setOpen(true);
  };

  const makeCredit = async (inv) => {
    if (!window.confirm(t("credit_note_confirm"))) return;
    try { const { data } = await api.post(`/invoices/${inv.id}/credit-note`); await load(); toast.success(`${t("credit_note_created")} ${data.number}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const send = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/send`);
      await load();
      toast.success(`${t("sent")} → ${data.to} (${data.language.toUpperCase()})`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/invoices/reminders/run");
      toast.success(`${t("invoice_reminders_done")}: ${data.sent}`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const printInvoice = (id) => window.open(`/print-factuur?invoice=${id}`, "_blank", "noopener");
  const openPdf = (id) => window.open(`${API}/invoices/${id}/pdf`, "_blank", "noopener");

  const markPaid = async (id, method) => {
    try {
      await api.patch(`/invoices/${id}/pay`, { method });
      await load();
      setPay(null);
      toast.success(t("invoice_marked_paid"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const markUnpaid = async (id) => {
    try { await api.patch(`/invoices/${id}/unpay`); await load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const copyPayLink = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.id}/payment-link`);
      try { await navigator.clipboard.writeText(data.short_url); } catch { /* clipboard geweigerd */ }
      toast.success(`${t("pay_link_copied")}: ${data.short_url}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/invoices/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const renderRows = (list) => list.map((inv) => (
    <TableRow key={inv.id} data-testid={`invoice-row-${inv.number}`} className={inv.status === "paid" ? "bg-emerald-50/40 dark:bg-emerald-950/10" : ""}>
      <TableCell className="font-semibold text-primary">
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1.5">
            {inv.number}
            {isExported(inv) && <Lock className="h-3 w-3 text-muted-foreground" data-testid={`locked-${inv.number}`} title={t("accounting_locked")} />}
          </span>
          {inv.doc_type === "credit" && <span className="inline-flex w-fit px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300" data-testid={`credit-badge-${inv.number}`}>{t("credit_note")} ← {inv.credit_of_number}</span>}
          {inv.credited_by && <span className="inline-flex w-fit px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" data-testid={`credited-badge-${inv.number}`}>{t("credited")} → {inv.credited_by_number}</span>}
        </div>
      </TableCell>
      <TableCell>{inv.guest_name}</TableCell>
      <TableCell className="uppercase text-xs font-semibold">{inv.language}</TableCell>
      <TableCell className="font-semibold">{money(inv.total, inv.currency)}</TableCell>
      <TableCell className="text-muted-foreground">{fmtDate(inv.created_at)}</TableCell>
      <TableCell>
        {inv.doc_type === "credit"
          ? <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300" data-testid={`status-credit-${inv.number}`}>{t("credit_note")}</span>
          : inv.status === "credited"
            ? <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" data-testid={`status-credited-${inv.number}`}>{t("credited")}</span>
            : <InvBadge status={inv.status} />}
        {inv.doc_type !== "credit" && inv.status === "paid" && inv.paid_at && (
          <span className="ml-2 text-[11px] text-muted-foreground" data-testid={`paid-info-${inv.number}`}>
            {fmtDate(inv.paid_at)} · {t(`pay_${inv.payment_method || "cash"}`)}
          </span>
        )}
        {inv.status !== "paid" && inv.status !== "credited" && inv.doc_type !== "credit" && inv.dunning_level > 0 && (() => {
          const last = (inv.dunning_sent || [])[(inv.dunning_sent || []).length - 1];
          return (
            <span
              className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${inv.dunning_level >= 3 ? "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" : inv.dunning_level === 2 ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" : "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300"}`}
              data-testid={`dunning-badge-${inv.number}`}
              title={(inv.dunning_sent || []).map((d) => `${t(`dunning_tier_name_${d.tier}`)} · ${fmtDate(d.at)}`).join("\n")}
            >
              <Bell className="h-3 w-3" />{t(`dunning_tier_name_${inv.dunning_level}`)}
              {last?.at && <span className="font-normal opacity-80">· {fmtDate(last.at)}</span>}
            </span>
          );
        })()}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {inv.doc_type !== "credit" && (inv.status === "paid"
          ? <Button variant="ghost" size="sm" onClick={() => markUnpaid(inv.id)} data-testid={`unpay-invoice-${inv.number}`} title={t("invoice_unpay")}><RotateCcw className="h-4 w-4 text-amber-600" /></Button>
          : <Button variant="ghost" size="sm" onClick={() => { setPay(inv); setPayMethod("cash"); }} data-testid={`pay-invoice-${inv.number}`} title={t("invoice_mark_paid")}><Check className="h-4 w-4 text-emerald-600" /></Button>)}
        <Button variant="ghost" size="sm" onClick={() => setView(inv)} data-testid={`view-invoice-${inv.number}`}><Eye className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => openPdf(inv.id)} data-testid={`pdf-invoice-${inv.number}`} title="PDF"><FileText className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => printInvoice(inv.id)} data-testid={`print-invoice-${inv.number}`} title={t("print")}><Printer className="h-4 w-4" /></Button>
        {inv.doc_type !== "credit" && inv.status !== "paid" && (
          <Button variant="ghost" size="sm" onClick={() => copyPayLink(inv)} data-testid={`paylink-invoice-${inv.number}`} title={t("pay_link")}>
            <Link2 className="h-4 w-4 text-emerald-600" />
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => send(inv.id)} data-testid={`send-invoice-${inv.number}`}><Send className="h-4 w-4 text-primary" /></Button>
        {isEditable(inv) && (
          <Button variant="ghost" size="sm" onClick={() => openEdit(inv)} data-testid={`edit-invoice-${inv.number}`} title={t("edit")}><Pencil className="h-4 w-4 text-sky-600" /></Button>
        )}
        {inv.doc_type !== "credit" && !inv.credited_by && (
          <Button variant="ghost" size="sm" onClick={() => makeCredit(inv)} data-testid={`credit-invoice-${inv.number}`} title={t("credit_note")}><FileMinus className="h-4 w-4 text-indigo-600" /></Button>
        )}
        {!isExported(inv) && !inv.credited_by && (
          <Button variant="ghost" size="sm" onClick={() => remove(inv.id)} data-testid={`delete-invoice-${inv.number}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
        )}
      </TableCell>
    </TableRow>
  ));

  const renderTable = (list) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("invoice_number")}</TableHead>
          <TableHead>{t("guest")}</TableHead>
          <TableHead>{t("doc_language")}</TableHead>
          <TableHead>{t("total")}</TableHead>
          <TableHead>{t("date")}</TableHead>
          <TableHead>{t("status")}</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{renderRows(list)}</TableBody>
    </Table>
  );

  const unpaid = rows.filter((r) => r.status !== "paid");
  const paid = rows.filter((r) => r.status === "paid");
  const dunned2 = unpaid.filter((r) => (r.dunning_level || 0) >= 2);
  const dunned3 = unpaid.filter((r) => (r.dunning_level || 0) >= 3);
  const filteredDun = dunFilter === "2plus" ? dunned2 : dunFilter === "3" ? dunned3 : null;

  const dunTabs = [
    { key: "all", label: t("dunning_filter_all"), count: rows.length },
    { key: "2plus", label: t("dunning_tier_name_2") + "+", count: dunned2.length },
    { key: "3", label: t("dunning_tier_name_3"), count: dunned3.length },
  ];

  return (
    <Panel
      title={t("invoices")}
      testid="invoices-panel"
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runReminders} data-testid="run-reminders-button">
            <Bell className="h-4 w-4 mr-1" />{t("invoice_reminders_run")}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="add-invoice-button"><Plus className="h-4 w-4 mr-1" />{t("new_invoice")}</Button>
        </div>
      }
    >
      {rows.length === 0 ? <Empty /> : (
        <div className="space-y-6 p-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="dunning-filter">
            <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Bell className="h-3.5 w-3.5" />{t("dunning_filter_label")}
            </span>
            {dunTabs.map((tab) => (
              <Button
                key={tab.key}
                variant={dunFilter === tab.key ? "default" : "outline"}
                size="sm"
                className="h-7 rounded-full text-xs"
                onClick={() => setDunFilter(tab.key)}
                data-testid={`dunning-filter-${tab.key}`}
              >
                {tab.label} <span className="ml-1.5 opacity-70">{tab.count}</span>
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="h-7 rounded-full text-xs ml-auto"
              onClick={() => window.open(`/print-aanmaningen?scope=${dunFilter === "3" ? "incasso" : "summary"}`, "_blank", "noopener")}
              data-testid="print-dunning-button"
            >
              <Printer className="h-3.5 w-3.5 mr-1" />
              {dunFilter === "3" ? t("dunning_print_incasso") : t("dunning_print_summary")}
            </Button>
          </div>
          {filteredDun ? (
            <section data-testid="invoices-dunned">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-rose-700">
                <span className="h-2 w-2 rounded-full bg-rose-500" />
                {dunFilter === "3" ? t("dunning_tier_name_3") : `${t("dunning_tier_name_2")}+`} ({filteredDun.length})
              </h3>
              {filteredDun.length ? renderTable(filteredDun) : <p className="text-sm text-muted-foreground px-1" data-testid="no-dunned">{t("dunning_none")}</p>}
            </section>
          ) : (
            <>
              <section data-testid="invoices-unpaid">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-amber-700">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />{t("invoices_unpaid")} ({unpaid.length})
                </h3>
                {unpaid.length ? renderTable(unpaid) : <p className="text-sm text-muted-foreground px-1" data-testid="no-unpaid">{t("no_unpaid_invoices")}</p>}
              </section>
              <section data-testid="invoices-paid">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-emerald-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />{t("invoices_paid")} ({paid.length})
                </h3>
                {paid.length ? renderTable(paid) : <p className="text-sm text-muted-foreground px-1" data-testid="no-paid">{t("no_paid_invoices")}</p>}
              </section>
            </>
          )}
        </div>
      )}

      {/* Create */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm({ guest_id: "", language: "nl", tax_rate: 21, currency: "EUR", items: [emptyItem()] }); } }}>
        <DialogContent className="max-w-2xl" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-display">{editingId ? t("edit_invoice") : t("new_invoice")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1 space-y-1.5">
                <Label className="text-xs">{t("guest")}</Label>
                <Select value={form.guest_id} onValueChange={onGuest} disabled={!!editingId}>
                  <SelectTrigger data-testid="invoice-guest"><SelectValue placeholder={t("select_guest")} /></SelectTrigger>
                  <SelectContent>{guests.map((g) => <SelectItem key={g.id} value={g.id}>{g.first_name} {g.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("doc_language")}</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })} disabled={!!editingId}>
                  <SelectTrigger data-testid="invoice-language"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{langName(l.code, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("tax")} %</Label>
                <Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: Number(e.target.value) })} data-testid="invoice-tax" />
              </div>
            </div>

            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={it._uid} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6 space-y-1"><Label className="text-[11px]">{t("description")}</Label><Input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`item-desc-${idx}`} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[11px]">{t("qty")}</Label><Input type="number" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} data-testid={`item-qty-${idx}`} /></div>
                  <div className="col-span-3 space-y-1"><Label className="text-[11px]">{t("unit_price")}</Label><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} data-testid={`item-price-${idx}`} /></div>
                  <div className="col-span-1"><Button variant="ghost" size="sm" onClick={() => removeItem(idx)} data-testid={`item-remove-${idx}`}><X className="h-4 w-4" /></Button></div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-line-button"><Plus className="h-4 w-4 mr-1" />{t("add_line")}</Button>
            </div>

            <div className="border-t border-border pt-3 text-sm space-y-1 text-right">
              <p className="text-muted-foreground">{t("subtotal")}: <span className="font-medium text-foreground">{money(subtotal)}</span></p>
              <p className="text-muted-foreground">{t("tax")} ({form.tax_rate}%): <span className="font-medium text-foreground">{money(tax)}</span></p>
              <p className="font-display font-bold text-lg text-primary">{t("total")}: {money(total)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={!form.guest_id} data-testid="save-invoice-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg" data-testid="invoice-view-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("invoice")} {view?.number}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>{view.guest_name}</span><span>{fmtDate(view.created_at)}</span>
              </div>
              <div className="border rounded-lg divide-y">
                {view.items.map((it, i) => (
                  <div key={i} className="flex justify-between px-3 py-2">
                    <span>{it.description} <span className="text-muted-foreground">× {it.qty}</span></span>
                    <span>{money(it.qty * it.unit_price, view.currency)}</span>
                  </div>
                ))}
              </div>
              <div className="text-right space-y-0.5">
                <p className="text-muted-foreground">{t("subtotal")}: {money(view.subtotal, view.currency)}</p>
                <p className="text-muted-foreground">{t("tax")} ({view.tax_rate}%): {money(view.tax_amount, view.currency)}</p>
                <p className="font-display font-bold text-lg text-primary">{t("total")}: {money(view.total, view.currency)}</p>
                {view.deposit_credit > 0 && (
                  <>
                    <p className="text-emerald-600" data-testid="view-deposit-credit">{t("deposit_paid")}: −{money(view.deposit_credit, view.currency)}</p>
                    <p className="font-display font-bold text-lg text-primary" data-testid="view-amount-due">{t("amount_due")}: {money(view.amount_due, view.currency)}</p>
                  </>
                )}
              </div>
              {view.dunning_sent?.length > 0 && (
                <div className="border rounded-lg p-3 space-y-1" data-testid="view-dunning-history">
                  <p className="flex items-center gap-1.5 font-semibold text-sm"><Bell className="h-4 w-4 text-amber-600" />{t("dunning_label")}</p>
                  <ul className="divide-y">
                    {view.dunning_sent.map((d, i) => (
                      <li key={i} className="flex items-center justify-between py-1.5 text-xs" data-testid={`dunning-item-${d.tier}`}>
                        <span className="font-medium">{t(`dunning_tier_name_${d.tier}`)}</span>
                        <span className="text-muted-foreground">{fmtDate(d.at)}{typeof d.overdue === "number" ? ` · ${d.overdue} ${t("dunning_overdue")}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" onClick={() => printInvoice(view.id)} data-testid="view-print-button"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
                <Button variant="outline" onClick={() => openPdf(view.id)} data-testid="view-pdf-button"><FileText className="h-4 w-4 mr-1" />PDF</Button>
                <Button onClick={() => send(view.id)} data-testid="view-send-button"><Send className="h-4 w-4 mr-1" />{t("send_email")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Betaald afvinken */}
      <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>
        <DialogContent className="max-w-sm" data-testid="invoice-pay-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("invoice_mark_paid")} — {pay?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{money(pay?.total || 0, pay?.currency)} · {pay?.guest_name}</p>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("payment_method")}</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "card", "transfer", "other"].map((m) => (
                    <SelectItem key={m} value={m} data-testid={`pay-method-${m}`}>{t(`pay_${m}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("invoice_paid_today")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPay(null)}>{t("cancel")}</Button>
            <Button onClick={() => markPaid(pay.id, payMethod)} data-testid="confirm-pay-button">
              <Check className="h-4 w-4 mr-1" />{t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
